import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { transformPlayerData } from '@/utils/playerTransform';
import { Player } from '@/types/player';
import { useAuth } from '@/contexts/AuthContext';

export const useCurrentUserPlayer = (teamId: string | null) => {
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(false);
  const { profile } = useAuth();

  const fetchCurrentUserPlayer = useCallback(async () => {
    if (!teamId) {
      setPlayer(null);
      return;
    }

    if (!profile) {
      setPlayer(null);
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('players')
        .select(`
          *,
          profile:profiles(first_name, last_name, email)
        `)
        .eq('team_id', teamId)
        .eq('profile_id', profile.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No player found for current user in this team
          setPlayer(null);
          return;
        }
        console.error('Error fetching current user player:', error);
        toast.error('Failed to load player data');
        return;
      }

      const transformedPlayer = transformPlayerData(data);
      
      setPlayer(transformedPlayer);
    } catch (error) {
      console.error('Error fetching current user player:', error);
      toast.error('Failed to load player data');
    } finally {
      setLoading(false);
    }
  }, [teamId, profile]);

  useEffect(() => {
    fetchCurrentUserPlayer();
  }, [fetchCurrentUserPlayer]);

  return {
    player,
    loading,
    refetch: fetchCurrentUserPlayer
  };
};